import type {
	Experience,
	ExperienceManifest,
	ExperienceManifestId,
	NativeEmbedClientId,
} from '../../common/types';
import type { Surface } from '../../common/types/surface/types';
import type { URLMetaData } from '../../common/types/url/types';
import {
	type ExperienceManifestRegistry,
	getExperienceManifestRegistry,
} from '../experience-manifest-registry/main';
import { getSurfaceService, type SurfaceService } from '../surface-service/main';

import type { SetupConfig } from './types';

let nativeEmbedFacade: NativeEmbedFacade | undefined;

export function getNativeEmbedFacade(): NativeEmbedFacade {
	if (!nativeEmbedFacade) {
		nativeEmbedFacade = new NativeEmbedFacade(getExperienceManifestRegistry(), getSurfaceService());
	}

	return nativeEmbedFacade;
}

export type ExperienceFacadeResult = {
	experience: Experience;
	manifest: ExperienceManifest;
};

export class NativeEmbedFacade {
	private experienceManifestRegistry: ExperienceManifestRegistry;
	private surfaceService: SurfaceService;
	private isSetUp = false;

	constructor(
		experienceManifestRegistry: ExperienceManifestRegistry,
		surfaceService: SurfaceService,
	) {
		this.experienceManifestRegistry = experienceManifestRegistry;
		this.surfaceService = surfaceService;
	}

	/**
	 * Registers an experience manifest so it can be matched by URL via {@link resolveManifestForUrl}.
	 */
	registerExperienceManifest(manifest: ExperienceManifest): void {
		this.experienceManifestRegistry.registerExperienceManifest(manifest);
	}

	/**
	 * One-shot convenience method that registers experience manifests and
	 * optionally configures the host surface in a single call.
	 *
	 * Prefer `setup()` over calling `registerExperienceManifest` /
	 * `setSurface` individually — it makes the configuration declarative,
	 * validates inputs eagerly, and guards against accidental double-setup.
	 *
	 * The low-level methods remain available for advanced use cases
	 * (conditional registration, dynamic surface changes, etc.).
	 *
	 * @param config - The setup configuration.
	 * @param config.experiences - Non-empty array of experience manifests to register.
	 * @param config.surface - Optional surface configuration.
	 *
	 * Logs a warning and returns early if `setup()` has already been called.
	 *
	 * @example
	 * ```ts
	 * import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
	 *
	 * getNativeEmbedFacade().setup({
	 *   experiences: [WhiteboardExperienceManifest, DBExperienceManifest],
	 * });
	 * ```
	 */
	setup(config: SetupConfig): void {
		if (this.isSetUp) {
			// eslint-disable-next-line no-console
			console.warn(
				'NativeEmbedFacade.setup() has already been called. ' +
					'This call will be ignored. ' +
					'Use the low-level registerExperienceManifest() and setSurface() methods ' +
					'if you need to modify configuration after initial setup.',
			);
			return;
		}

		for (const manifest of config.experiences) {
			this.registerExperienceManifest(manifest);
		}

		if (config.surface) {
			this.setSurface(config.surface);
		}

		this.isSetUp = true;
	}

	/**
	 * Synchronous function that verifies if the URL matches any registered experience, and returns the experience ID if it does.
	 * @param url - The URL to get the experience from.
	 * @returns
	 */
	getExperienceIdFromUrl(url: string): ExperienceManifestId | undefined {
		return this.experienceManifestRegistry.getExperienceIdFromUrl(url);
	}

	/**
	 * Synchronous function that returns the manifest for a URL if it matches any registered experience.
	 * Uses `isUrlMatch` to find the matching manifest.
	 * @param url - The URL to get the manifest for.
	 * @returns The matching manifest, or undefined if no match is found.
	 */
	getManifestByUrl(url: string): ExperienceManifest | undefined {
		return this.experienceManifestRegistry.getManifestByUrl(url);
	}

	/**
	 * Returns a list of all registered experience IDs.
	 */
	getAllExperienceIds(): ExperienceManifestId[] {
		return this.experienceManifestRegistry.getAllExperienceIds();
	}

	/**
	 * Register or replace the active host surface.
	 *
	 * The surface provides capabilities that differ per host (editor, renderer,
	 * etc.). The {@link Surface} interface will grow as new surface concerns
	 * are identified.
	 *
	 * - Replaces any previously registered surface (last-write-wins).
	 * - Pass `null` to clear the surface (e.g. on unmount).
	 *
	 * @param surface - The surface configuration, or `null` to clear.
	 */
	setSurface(surface: Surface | null): void {
		this.surfaceService.setSurface(surface);
	}

	/**
	 * Finds the matching manifest for a URL and creates an experience.
	 * Title metadata will be resolved later by Smart Card.
	 */
	async createExperienceForUrl(url: string): Promise<ExperienceFacadeResult | undefined> {
		const manifest = this.experienceManifestRegistry.getManifestByUrl(url);
		if (!manifest) {
			return undefined;
		}
		const urlMeta: URLMetaData = { url, title: '' };
		// Lazy import — ExperienceService transitively pulls in
		// rovo-content-bridge-api which uses uuid/randomBytes. Keeping it out of
		// the static import graph prevents the SSR pre-compile crash.
		const { getExperienceService } = await import('../experience-service/main');
		const experience = getExperienceService().registerExperience({ manifest, urlMeta });
		return {
			experience,
			manifest,
		};
	}

	/**
	 * Unregister an experience and dispose its event listeners.
	 *
	 * Call when the embed is removed from the host surface (e.g. editor node
	 * deleted, component unmount). This prevents memory leaks from stale
	 * listener references.
	 *
	 * Safe to call with an ID that was never registered — it's a no-op.
	 */
	async destroyExperience(experienceId: NativeEmbedClientId): Promise<void> {
		const { getExperienceService } = await import('../experience-service/main');
		getExperienceService().unregisterExperience(experienceId);
	}
}
